import { Checkbox as AntCheckbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import React from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography/Typography';

export interface CheckboxProps {
  checked?: boolean;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  icon?: IconType;
  indeterminate?: boolean;
  label?: string;
  onChange?: (checked: boolean) => void;
  textHelper?: string;
}

const StyledTextHelper = styled(Typography)`
  color: ${theme.dime.colors.text.secondary};
  display: flex;
  align-items: flex-start;
  gap: ${theme.dime.spacing.s};
  padding-left: ${theme.dime.spacing.xl};
`;

const StyledTypography = styled(Typography)<{ $hasError: boolean }>`
  color: ${(props) =>
    props.$hasError
      ? theme.dime.colors.text.critical
      : theme.dime.colors.text.primary};
`;

const StyledCheckboxWrapper = styled.div<{
  $hasError: boolean;
  $checked: boolean;
  $disabled: boolean;
}>`
  .ant-checkbox-wrapper {
    .ant-checkbox {
      position: relative;

      .ant-checkbox-inner {
        position: relative;

        ${(props) =>
          props.$checked &&
          !props.$disabled &&
          !props.$hasError &&
          `
           background-color: ${theme.dime.colors.action.primary};
          border-color: ${theme.dime.colors.action.primary};
        `}

        ${(props) =>
          props.$checked &&
          !props.$disabled &&
          props.$hasError &&
          `
          background-color: ${theme.dime.colors.fill.criticalPrimary};
          border-color: ${theme.dime.colors.border.critical};
        `}

        ${(props) =>
          props.$checked &&
          props.$disabled &&
          `
          background-color: ${theme.dime.colors.border.interactiveDisabled};
          border-color: ${theme.dime.colors.border.interactiveDisabled};
        `}
      }

      &.ant-checkbox-indeterminate .ant-checkbox-inner {
        border-color: ${theme.dime.colors.border.interactiveDisabled};
      }

      &.ant-checkbox-indeterminate:hover .ant-checkbox-inner::after {
        background-color: ${theme.dime.colors.action.primaryHover};
      }

      &:hover .ant-checkbox-inner {
        ${(props) =>
          props.$checked &&
          !props.$disabled &&
          !props.$hasError &&
          `
          background-color: ${theme.dime.colors.action.primaryHover};
          border-color: ${theme.dime.colors.action.primaryHover};
        `}

        ${(props) =>
          props.$checked &&
          !props.$disabled &&
          props.$hasError &&
          `
          background-color: ${theme.dime.colors.action.criticalPrimaryHover};
          border-color: ${theme.dime.colors.action.criticalPrimaryHover};
        `}

        ${(props) =>
          !props.$checked &&
          !props.$disabled &&
          !props.$hasError &&
          `
          border-color: ${theme.dime.colors.border.interactive};
        `}
      }

      &.ant-checkbox-checked .ant-checkbox-inner::after {
        display: none;
      }

      &.ant-checkbox-checked:hover .ant-checkbox-inner {
        ${(props) =>
          props.$hasError
            ? `
          background-color: ${theme.dime.colors.action.criticalPrimaryHover};
          border-color: ${theme.dime.colors.action.criticalPrimaryHover};
        `
            : `
          background-color: ${theme.dime.colors.action.primaryHover};
          border-color: ${theme.dime.colors.action.primaryHover};
        `}
      }
    }

    &:hover .ant-checkbox:not(.ant-checkbox-checked) .ant-checkbox-inner {
      ${(props) =>
        !props.$disabled &&
        `
        border-color: ${theme.dime.colors.border.interactive};
      `}
    }

    ${(props) =>
      props.$hasError &&
      `
      .ant-checkbox-inner {
        border-color: ${theme.dime.colors.border.critical};
      }
    `}
  }
`;

const CheckboxContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  .custom-check-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`;

export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  className,
  disabled = false,
  hasError = false,
  icon,
  indeterminate = false,
  label,
  onChange,
  textHelper
}) => {
  const handleChange = (e: CheckboxChangeEvent) => {
    if (onChange) {
      onChange(e.target.checked);
    }
  };

  return (
    <>
      <StyledCheckboxWrapper
        className={className}
        $hasError={hasError}
        $checked={checked}
        $disabled={disabled}
      >
        <CheckboxContainer>
          <AntCheckbox
            checked={checked}
            indeterminate={indeterminate}
            disabled={disabled}
            onChange={handleChange}
          >
            {!!icon && (
              <IconWrap
                type={icon}
                size={IconSize.Small}
                style={{
                  color: disabled
                    ? theme.dime.colors.icon.disabled
                    : theme.dime.colors.icon.primary
                }}
              />
            )}
            <StyledTypography variant="body1" $hasError={hasError}>
              {label}
            </StyledTypography>
          </AntCheckbox>
          {!!checked && !indeterminate && (
            <div className="custom-check-icon">
              <IconWrap
                type={IconType.Approve}
                size={IconSize.Small}
                style={{
                  color: disabled
                    ? theme.dime.colors.text.disabled
                    : theme.dime.colors.text.inverse
                }}
              />
            </div>
          )}
        </CheckboxContainer>
      </StyledCheckboxWrapper>
      {!!textHelper && (
        <StyledTextHelper
          variant="body1"
          color={theme.dime.colors.text.secondary}
        >
          {textHelper}
        </StyledTextHelper>
      )}
    </>
  );
};
